import { createContext, useContext, useMemo, useState } from 'react';
import type { RequestArgs, RequestBody, RequestFailure } from '@ff-it/api';
import type { PlanResponse, PlanSettings, ReorderRequest, RowOrGroup } from '../types';
import type { RefObject } from 'react';
import type { GridHandle } from '../Grid';
import { Fetcher, FetchResult } from 'services';
import { __planAtom, planAtom, planResponseSetterAtom, type PlanStore } from './plan';
import invariant from 'tiny-invariant';
import { RowsChangeData } from 'components/DataGrid';
import { applyPlanDiff, PlanDiffResponse, PlanDiffRequest } from '../diff';
import type { ProductKind } from 'types';
import { checkActionError, parseAttachmentFilename } from 'utilities';
import { useStore } from 'jotai';
import type { PlanRow } from 'modules/campaign/row';

import { mutate as mutateGlobal } from 'swr';
import { SUMS_KEY } from '../useBlockSums';
import { getDRFFormError, type SubmissionErrors } from '@ff-it/form';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import type { ExportAction, ImportAction } from '../io';
import { optimisticReorder } from './reorder';
import { BlockDetails } from 'modules/campaign/block/types';
import { EntityContextType } from 'components';
import { createRevisionLoader } from './revisionLoader';
import { updateBriefSettings } from './brief';

interface MutateOptions<TResponse> {
  onSuccess?: (resp: TResponse) => void;
  optimistic?: (current: PlanStore) => PlanStore;
}

type RunMutation = <T>(args: RequestArgs, options: MutateOptions<T>) => Promise<FetchResult<T>>;

export type PlanMutations = {
  loadPlan: (revision: number) => Promise<void>;
  requestHandler: Fetcher;
  selectCell: GridHandle['selectCell'];
  runMutation: RunMutation;
  // new bulk api
  update: <T = PlanDiffResponse>(
    diff: Partial<PlanDiffRequest>,
    mapResponse?: (response: PlanDiffResponse, prevState: PlanStore) => T,
  ) => Promise<FetchResult<T>>;
  applyDiff: (response: PlanDiffResponse) => void;
  setPlan: (response: PlanResponse) => void;

  // rows
  reorder: (request: ReorderRequest) => Promise<void>;
  addRow: (kind: ProductKind, parent_id?: number) => Promise<void>;
  onRowsChange: (updatedRows: RowOrGroup[], data: RowsChangeData<RowOrGroup, any>) => Promise<void>;
  copyRows: (rows: PlanRow[]) => Promise<PlanRow[]>;
  updateSettings: (settings: PlanSettings) => Promise<SubmissionErrors | void>;

  // io
  exportHandler: (action: ExportAction, body?: RequestBody) => Promise<void>;
  importHandler: (action: ImportAction, body: RequestBody) => Promise<SubmissionErrors | void>;
};

export function useCreatePlanMutations(
  requestHandler: Fetcher,
  gridRef: RefObject<GridHandle>,
  mutate: EntityContextType<BlockDetails>['mutate'],
): PlanMutations {
  const store = useStore();
  const [error, setError] = useState();
  if (error) {
    throw error;
  }
  return useMemo(() => {
    const { get, set } = store;

    const applyDiff = (response: PlanDiffResponse) => {
      set(__planAtom, applyPlanDiff(get(planAtom), response));
    };
    const setPlan = (planResponse: PlanResponse) => {
      set(planResponseSetterAtom, planResponse);
    };

    const handleError = (res: any) => {
      if (!checkActionError(res)) {
        setError(res.error);
      }
    };

    const unwrapOrHandle = <T>(result: FetchResult<T>): T | undefined => {
      try {
        return result.unwrap();
      } catch (error) {
        handleError(error);
      }
    };

    const runMutation = async <T>(args: RequestArgs, options: MutateOptions<T> = {}): Promise<FetchResult<T>> => {
      const { onSuccess, optimistic } = options;

      // Snapshot
      const snapshot = optimistic ? get(planAtom) : null;
      if (optimistic && snapshot) set(__planAtom, optimistic(snapshot));

      try {
        const response = await requestHandler<T>(args);

        onSuccess?.(response);
        if (args.method !== 'GET') {
          mutateGlobal(SUMS_KEY);
        }

        return FetchResult.ok(response);
      } catch (error) {
        // Rollback
        if (snapshot) set(__planAtom, snapshot);
        return FetchResult.err(error);
      }
    };

    const loadPlan = createRevisionLoader(
      () => requestHandler<PlanResponse>({ method: 'GET', url: `plan/` }),
      (raw) => set(planResponseSetterAtom, raw),
    );

    return {
      requestHandler,
      runMutation,
      // @TODO optimistc
      update: async <T = PlanDiffResponse>(
        diff: Partial<PlanDiffRequest>,
        mapResponse?: (response: PlanDiffResponse, prevState: PlanStore) => T,
      ): Promise<FetchResult<T>> => {
        const prevState = mapResponse ? get(planAtom) : null;

        const result = await runMutation<PlanDiffResponse>(
          {
            url: 'plan/update/',
            method: 'POST',
            body: diff,
          },
          { onSuccess: applyDiff },
        );

        if (mapResponse) {
          // prevState is guaranteed to be PlanStore here because mapper exists
          return result.map((response) => mapResponse(response, prevState!));
        }

        return result as unknown as FetchResult<T>;
      },
      applyDiff,
      setPlan,
      loadPlan,
      selectCell(rowKey, columnKey, enableEditor, scrollToCell) {
        gridRef.current?.selectCell(rowKey, columnKey, enableEditor, scrollToCell);
      },
      reorder: async (request: ReorderRequest) => {
        const { subjectId, subjectType, targetId, targetType, position } = request;
        unwrapOrHandle(
          await runMutation<PlanDiffResponse>(
            {
              method: 'POST',
              url: `plan/move/`,
              body: {
                subject_id: subjectId,
                subject_type: subjectType,
                target_id: targetId,
                target_type: targetType,
                position,
              },
            },
            {
              onSuccess: applyDiff,
              optimistic: (current) => optimisticReorder(current, request),
            },
          ),
        );

        gridRef.current?.selectCell(`${subjectType === 'GROUP' ? 'group' : 'row'}-${subjectId}`, 'title', false, false);
      },

      addRow: async (kind: ProductKind, parent_id?: number) => {
        const existingIds = new Set(Object.keys(get(planAtom)['rows']).map(Number));

        const resp = unwrapOrHandle(
          await runMutation<PlanDiffResponse>(
            {
              method: 'POST',
              url: `plan/rows/`,
              body: { kind, group_id: parent_id },
            },
            { onSuccess: applyDiff },
          ),
        );

        if (resp) {
          const result = (resp['rows']?.upsert ?? []).filter((r) => !existingIds.has(r.id));
          gridRef.current?.selectCell(`row-${result[result.length - 1].id}`);
        }
      },

      /**
       * This is what is called for editors that call onRowChange and commit
       */
      onRowsChange: async (updatedRows: RowOrGroup[], { indexes, column }: RowsChangeData<RowOrGroup, any>) => {
        // should be single row
        invariant(indexes.length === 1);
        const scheduledRow: RowOrGroup = updatedRows[indexes[0]];
        if (scheduledRow._isGroup) {
          unwrapOrHandle(
            await runMutation(
              {
                url: 'plan/update/',
                method: 'POST',
                body: { groups: { upsert: [scheduledRow] } },
              },
              {
                onSuccess: applyDiff,
                optimistic: (current) => ({
                  ...current,
                  groups: { ...current.groups, [scheduledRow.id]: { ...scheduledRow } },
                }),
              },
            ),
          );
        } else {
          let field = column.key as keyof PlanRow;
          // FIXME
          if (column.key === 'smart') {
            field = scheduledRow.kind === 'SMART' ? 'rate' : 'smart';
          }

          const payload = scheduledRow[field];
          unwrapOrHandle(
            await runMutation(
              {
                url: 'plan/update/',
                method: 'POST',
                body: {
                  rows: {
                    upsert: [{ id: scheduledRow.id, [field]: payload }],
                  },
                },
              },
              {
                onSuccess: applyDiff,
                optimistic: (current) => ({
                  ...current,
                  rows: { ...current.rows, [scheduledRow.id]: { ...scheduledRow } },
                }),
              },
            ),
          );
        }
      },

      copyRows: async (rows: PlanRow[]): Promise<PlanRow[]> => {
        const existingRowIds = new Set(Object.keys(get(planAtom).rows).map(Number));
        const resp = unwrapOrHandle(
          await runMutation<PlanDiffResponse>(
            {
              method: 'POST',
              url: 'plan/rows/copy/',
              body: { items: rows.map((r) => ({ source_id: r.id })), schedule: 'skip' },
            },
            { onSuccess: applyDiff },
          ),
        );
        invariant(resp);
        const result = (resp.rows?.upsert ?? []).filter((r) => !existingRowIds.has(r.id));
        if (result.length) gridRef.current?.selectCell(`row-${result[result.length - 1].id}`);
        return result;
      },
      updateSettings: async (settings: PlanSettings): Promise<SubmissionErrors | void> => {
        const result = await runMutation<PlanResponse>(
          { method: 'POST', url: `plan/settings/`, body: settings },
          {
            onSuccess(resp) {
              set(planResponseSetterAtom, resp);
              // FIXME: keep settings on block (brief) in sync
              mutate((currentData) => {
                if (!currentData) return currentData;
                return updateBriefSettings(currentData, resp);
              });
            },
          },
        );
        try {
          result.unwrap();
        } catch (error) {
          const formError = getDRFFormError(error as RequestFailure<any>);
          if (formError) {
            return formError as any; // ??
          }
          handleError(error);
        }
      },

      exportHandler: async (action: ExportAction, body?: RequestBody): Promise<void> => {
        let filename;
        const blob = await requestHandler<Blob>({
          method: 'POST',
          url: `export/${action}/`,
          body: body,
          headers: { Accept: '*/*' },
          // FIXME: we really need a better api client
          after: (r) => {
            if (r.response) {
              filename = parseAttachmentFilename(r.response.headers);
            }
          },
        }).catch(handleError);
        invariant(blob);
        saveAs(blob, filename);
      },
      importHandler: async (action: ImportAction, body?: RequestBody): Promise<SubmissionErrors | void> => {
        const res = await runMutation<PlanResponse>(
          {
            url: `import/${action}/`,
            body: body,
            method: 'POST',
          },
          {
            onSuccess(resp) {
              set(planResponseSetterAtom, resp);
              toast.success(`${action} has been imported`);
            },
          },
        );
        return res.unwrapFormError();
      },
    };
  }, [store, requestHandler, gridRef, mutate]);
}

export const PlanMutationContext = createContext<PlanMutations | undefined>(undefined);

export function usePlanMutations(): PlanMutations {
  const ctx = useContext(PlanMutationContext);
  invariant(ctx);
  return ctx;
}
